import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthContext, api } from '../context/AuthContext'

/* ─── Fonts ─────────────────────────────────────────────────── */
if (typeof document !== 'undefined') {
  const id = 'kudos-fonts'
  if (!document.getElementById(id)) {
    const l = document.createElement('link')
    l.id = id; l.rel = 'stylesheet'
    l.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap'
    document.head.appendChild(l)
  }
}

const C = {
  navy    : '#060D1F',
  navyMid : '#0D1835',
  navyLine: '#111D36',
  orange  : '#E8440A',
  white   : '#FFFFFF',
  snow    : '#F9F8F6',
  border  : '#EBEBEB',
  text    : '#0C0B09',
  textMed : '#52493C',
  textSoft: '#9A9182',
}
const F = "'Inter', 'Plus Jakarta Sans', sans-serif"

const Ion = ({ name, size = 18, color = 'currentColor', style: s = {} }) => (
  <ion-icon name={name} style={{ fontSize: size, color, display: 'block', flexShrink: 0, ...s }} />
)

function useIsMobile(bp = 768) {
  const [m, setM] = useState(() => typeof window !== 'undefined' ? window.innerWidth < bp : false)
  useEffect(() => {
    const h = () => setM(window.innerWidth < bp)
    window.addEventListener('resize', h)
    return () => window.removeEventListener('resize', h)
  }, [bp])
  return m
}

const Login = () => {
  const { saveToken }         = useAuthContext()
  const navigate              = useNavigate()
  const [phone, setPhone]     = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError]     = useState('')
  const [focused, setFocused] = useState(false)
  const mob                   = useIsMobile()

  const isValid = phone.replace(/\D/g, '').length >= 9

  const formatPhone = (val) => {
    const digits = val.replace(/\D/g, '').slice(0, 12)
    const parts = []
    if (digits.length > 0) parts.push(digits.slice(0, 3))
    if (digits.length > 3) parts.push(digits.slice(3, 6))
    if (digits.length > 6) parts.push(digits.slice(6, 12))
    return parts.join(' ')
  }

  const handleLogin = async (e) => {
    e?.preventDefault()
    if (!isValid) return
    setLoading(true)
    setError('')
    try {
      const res = await api.post('/user/admin/login', {
        phone: phone.replace(/\s/g, ''),
      })
      saveToken(res.data.token)
      navigate('/dashboard', { replace: true })
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed')
    } finally {
      setLoading(false)
    }
  }

  const borderColor = error ? C.orange : focused ? C.navy : C.border

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: mob ? 'column' : 'row', fontFamily: F }}>
      <style>{`
        @keyframes kpulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.3;transform:scale(1.6)} }
        @keyframes spin    { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        input::placeholder { color: #C8C0B0 }
        * { box-sizing: border-box; margin: 0; padding: 0 }
      `}</style>

      {/* ── Mobile top bar ── */}
      {mob && (
        <div style={{
          background: C.navy, padding: '18px 24px',
          display: 'flex', alignItems: 'center', gap: 10,
          borderBottom: `1px solid ${C.navyLine}`,
        }}>
          <img src="/kudos_logo.png" alt="Kudos Live" style={{
            width: 34, height: 34, borderRadius: 10, flexShrink: 0, objectFit: 'cover',
            boxShadow: '0 4px 14px rgba(232,68,10,0.30)',
          }} onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex' }} />
          <div style={{
            width: 34, height: 34, borderRadius: 10, flexShrink: 0,
            background: `linear-gradient(145deg, ${C.orange} 0%, #c9380a 100%)`,
            display: 'none', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 14px rgba(232,68,10,0.30)', position: 'relative', overflow: 'hidden',
          }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '50%', background: 'rgba(255,255,255,0.12)', pointerEvents: 'none' }} />
            <Ion name="bag-outline" size={17} color="#fff" style={{ '--ionicon-stroke-width': '44px' }} />
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 800, color: C.white, letterSpacing: '-0.03em', lineHeight: 1 }}>Kudos Live</div>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 2, fontWeight: 500 }}>Admin Dashboard</div>
          </div>
          <div style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 5, background: 'rgba(232,68,10,0.12)', border: '1px solid rgba(232,68,10,0.25)', borderRadius: 99, padding: '4px 10px' }}>
            <div style={{ width: 5, height: 5, borderRadius: '50%', background: C.orange, animation: 'kpulse 1.4s infinite' }} />
            <span style={{ fontSize: 9, fontWeight: 600, color: C.orange, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Live</span>
          </div>
        </div>
      )}

      {/* ── Form panel ── */}
      <div style={{
        width: mob ? '100%' : 500,
        flexShrink: 0,
        display: 'flex', flexDirection: 'column',
        justifyContent: 'center',
        padding: mob ? '40px 24px 60px' : '64px 60px',
        background: C.white,
        borderRight: mob ? 'none' : `1px solid ${C.border}`,
        flex: mob ? 1 : undefined,
      }}>

        {/* Logo — desktop only */}
        {!mob && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 52 }}>
            <img src="/kudos_logo.png" alt="Kudos Live" style={{
              width: 38, height: 38, borderRadius: 11, flexShrink: 0, objectFit: 'cover',
              boxShadow: '0 4px 16px rgba(232,68,10,0.28)',
            }} onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex' }} />
            <div style={{
              width: 38, height: 38, borderRadius: 11,
              background: `linear-gradient(145deg, ${C.orange} 0%, #c9380a 100%)`,
              display: 'none', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 4px 16px rgba(232,68,10,0.28)', flexShrink: 0, position: 'relative', overflow: 'hidden',
            }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '50%', background: 'rgba(255,255,255,0.12)', pointerEvents: 'none' }} />
              <Ion name="bag-outline" size={20} color="#fff" style={{ '--ionicon-stroke-width': '44px' }} />
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 800, color: C.text, letterSpacing: '-0.03em', lineHeight: 1 }}>Kudos Live</div>
              <div style={{ fontSize: 11, color: C.textSoft, marginTop: 2, fontWeight: 500 }}>Admin Dashboard</div>
            </div>
          </div>
        )}

        {/* Heading */}
        <h1 style={{ fontSize: mob ? 26 : 28, fontWeight: 900, color: C.text, letterSpacing: '-0.04em', lineHeight: 1.18, marginBottom: 8, marginTop: mob ? 4 : 0 }}>
          Welcome back
        </h1>
        <p style={{ fontSize: 14, color: C.textSoft, lineHeight: 1.65, marginBottom: 36, fontWeight: 400 }}>
          Enter your phone number to sign in to the Kudos Live admin panel.
        </p>

        {/* Form */}
        <form onSubmit={handleLogin}>

          <div style={{ marginBottom: 6 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: C.textMed, letterSpacing: '0.04em', textTransform: 'uppercase', display: 'block', marginBottom: 8 }}>
              Phone Number
            </label>
            <div style={{
              display: 'flex', alignItems: 'center',
              background: C.white,
              border: `1.5px solid ${borderColor}`,
              borderRadius: 12, paddingLeft: 14, height: 52,
              transition: 'border-color 0.15s',
              boxShadow: focused && !error ? `0 0 0 3px rgba(6,13,31,0.06)` : error ? '0 0 0 3px rgba(232,68,10,0.08)' : 'none',
            }}>
              {/* Flag + prefix */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                <div style={{ width: 20, height: 14, borderRadius: 2, overflow: 'hidden', flexShrink: 0, display: 'flex', flexDirection: 'column' }}>
                  <div style={{ flex: 5, background: '#20BCD4' }} />
                  <div style={{ flex: 2, background: '#FAD201' }} />
                  <div style={{ flex: 5, background: '#1EB53A' }} />
                </div>
                <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>+250</span>
              </div>
              <div style={{ width: 1, height: 20, background: C.border, margin: '0 12px', flexShrink: 0 }} />
              <input
                type="tel"
                value={phone}
                onChange={e => { setError(''); setPhone(formatPhone(e.target.value)) }}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                placeholder="7XX XXX XXX"
                maxLength={14}
                autoFocus
                style={{
                  flex: 1, border: 'none', outline: 'none',
                  fontSize: 16, fontWeight: 600, color: C.text,
                  letterSpacing: '0.04em', background: 'transparent',
                  fontFamily: F,
                }}
              />
              {phone.length > 0 && (
                <div style={{ paddingRight: 14, flexShrink: 0 }}>
                  <Ion
                    name={isValid ? 'checkmark-circle' : 'ellipse-outline'}
                    size={18}
                    color={isValid ? '#16A34A' : C.border}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Error */}
          {error && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, marginBottom: 4 }}>
              <Ion name="alert-circle-outline" size={14} color={C.orange} />
              <span style={{ fontSize: 12, color: C.orange, fontWeight: 500 }}>{error}</span>
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={!isValid || loading}
            style={{
              width: '100%', height: 50, borderRadius: 12, border: 'none',
              background: isValid && !loading
                ? `linear-gradient(145deg, ${C.orange} 0%, #c9380a 100%)`
                : '#F2C8BB',
              color: '#fff', fontSize: 15, fontWeight: 700,
              cursor: isValid && !loading ? 'pointer' : 'not-allowed',
              marginTop: 20, letterSpacing: '-0.01em',
              boxShadow: isValid && !loading ? '0 6px 20px rgba(232,68,10,0.28)' : 'none',
              fontFamily: F,
              transition: 'all 0.2s',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}
          >
            {loading
              ? <><Ion name="refresh-outline" size={16} color="#fff" style={{ animation: 'spin 1s linear infinite' }} /> Signing in…</>
              : <><span>Continue</span><Ion name="arrow-forward-outline" size={16} color="#fff" /></>
            }
          </button>
        </form>

        {/* Terms */}
        <p style={{ fontSize: 11, color: C.textSoft, textAlign: 'center', marginTop: 28, lineHeight: 1.8, fontWeight: 400 }}>
          By continuing you agree to our{' '}
          <span style={{ color: C.orange, fontWeight: 600, cursor: 'pointer' }}>Terms of Service</span>
          {' '}and{' '}
          <span style={{ color: C.orange, fontWeight: 600, cursor: 'pointer' }}>Privacy Policy</span>.
        </p>
      </div>

      {/* ── Right — navy branding panel (desktop only) ── */}
      {!mob && (
        <div style={{
          flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: C.navy, padding: 56, position: 'relative', overflow: 'hidden',
        }}>
          {/* Background mesh */}
          <div style={{ position: 'absolute', inset: 0, backgroundImage: `radial-gradient(ellipse 65% 55% at 60% 45%, rgba(232,68,10,0.1) 0%, transparent 70%), radial-gradient(ellipse 45% 50% at 15% 85%, rgba(29,78,216,0.1) 0%, transparent 60%)`, pointerEvents: 'none' }} />
          {/* Grid lines */}
          <div style={{ position: 'absolute', inset: 0, backgroundImage: `repeating-linear-gradient(0deg, transparent, transparent 71px, rgba(255,255,255,0.025) 71px, rgba(255,255,255,0.025) 72px), repeating-linear-gradient(90deg, transparent, transparent 71px, rgba(255,255,255,0.025) 71px, rgba(255,255,255,0.025) 72px)`, pointerEvents: 'none' }} />

          <div style={{ maxWidth: 360, textAlign: 'center', position: 'relative' }}>

            {/* Big icon */}
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 28 }}>
              <img src="/kudos_logo.png" alt="Kudos Live" style={{
                width: 72, height: 72, borderRadius: 22, objectFit: 'cover',
                boxShadow: '0 16px 48px rgba(232,68,10,0.30)',
              }} onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex' }} />
              <div style={{
                width: 72, height: 72, borderRadius: 22,
                background: `linear-gradient(145deg, ${C.orange} 0%, #c9380a 100%)`,
                display: 'none', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 16px 48px rgba(232,68,10,0.30)',
                position: 'relative', overflow: 'hidden',
              }}>
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '50%', background: 'rgba(255,255,255,0.1)', pointerEvents: 'none' }} />
                <Ion name="bag-outline" size={36} color="#fff" style={{ '--ionicon-stroke-width': '36px' }} />
              </div>
            </div>

            {/* Live dot */}
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: 'rgba(232,68,10,0.12)', border: '1px solid rgba(232,68,10,0.25)', borderRadius: 99, padding: '5px 13px', marginBottom: 22 }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: C.orange, animation: 'kpulse 1.4s infinite' }} />
              <span style={{ fontSize: 11, fontWeight: 600, color: C.orange, letterSpacing: '0.06em', textTransform: 'uppercase', fontFamily: F }}>Platform Live</span>
            </div>

            <h2 style={{ fontSize: 26, fontWeight: 900, color: C.white, letterSpacing: '-0.04em', lineHeight: 1.18, marginBottom: 12 }}>
              Kudos Live<br />Admin Panel
            </h2>
            <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.38)', lineHeight: 1.8, marginBottom: 44, fontWeight: 400 }}>
              Manage users, sellers, reels, live streams,<br />payments, drivers and more — all in one place.
            </p>

            {/* Stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
              {[
                { icon: 'people-outline',    val: '142K', label: 'Users'   },
                { icon: 'storefront-outline',val: '2.8K', label: 'Sellers' },
                { icon: 'cash-outline',      val: '$4.2M',label: 'GMV'     },
              ].map(({ icon, val, label }) => (
                <div key={label} style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 14, padding: '16px 12px' }}>
                  <Ion name={icon} size={18} color={C.orange} style={{ margin: '0 auto 8px' }} />
                  <div style={{ fontSize: 20, fontWeight: 800, color: C.white, letterSpacing: '-0.04em', fontFamily: F }}>{val}</div>
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.09em', fontWeight: 600, marginTop: 3, fontFamily: F }}>{label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Login
